import React from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { Input, InputAdornment, InputProps, Tooltip } from '@mui/material';
import { Info } from 'lucide-react';

type InputFieldProps = InputProps & {
  controller?: UseControllerProps<any>;
  dark?: boolean;
  error?: boolean;
  label?: string;
  hiddenLabel?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  infoText?: string;
  disableDecimal?: boolean;
  decimalScale?: number;
  stopPropagation?: boolean;
}

const InputNumber: React.FC<InputFieldProps> = ({
  dark = false,
  error = false,
  hiddenLabel,
  disabled,
  decimalScale = 3,
  disableDecimal = false,
  startAdornment,
  endAdornment,
  label,
  infoText,
  onChange,
  slotProps,
  fullWidth,
  value,
  defaultValue,
  type = 'text',
  controller,
  ...props
}) => {
  const backgroundClass = dark ? 'Mui-backgroundDark' : 'Mui-backgroundLight';

  return controller ? (
    <Controller
      {...controller}
      render={({ field, fieldState }) => {

        return (
          <Input
            {...props}
            type="number"
            className={backgroundClass}
            name={field.name}
            value={field.value}
            error={fieldState.invalid}
            onFocus={(event) => {
              event.target.select();
              props.onFocus && props.onFocus(event);
            }}
            onBlur={(data) => {
              field.onBlur();
              props.onBlur && props.onBlur(data);
            }}
            onChange={(data) => {
              field.onChange(data);
              onChange && onChange(data);
            }}
            disabled={disabled}
            fullWidth={fullWidth}
            slotProps={{
              ...slotProps,
              input: () => ({
                ref: props.inputRef,
                className: backgroundClass,
                error: fieldState.invalid,
                startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment>,
                endAdornment: (
                  <>
                    {endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                    {infoText && (
                      <InputAdornment position="end">
                        <Tooltip title={infoText}>
                          <Info />
                        </Tooltip>
                      </InputAdornment>
                    )}
                  </>
                ),
              }),
            }}
          />
        );
      }}
    />
  ) :
    (<Input
      {...props}
      type="number"
      className={backgroundClass}
      value={value}
      onFocus={(event) => {
        event.target.select();
        props.onFocus && props.onFocus(event);
      }}
      onBlur={(data) => {
        props.onBlur && props.onBlur(data);
      }}
      onChange={(data) => {
        onChange && onChange(data);
      }}
      disabled={disabled}
      fullWidth={fullWidth}
      slotProps={{
        ...slotProps,
        input: () => ({
          ref: props.inputRef,
          className: backgroundClass,
          startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment>,
          endAdornment: (
            <>
              {endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
              {infoText && (
                <InputAdornment position="end">
                  <Tooltip title={infoText}>
                    <Info />
                  </Tooltip>
                </InputAdornment>
              )}
            </>
          ),
        }),
      }}
    />)
}

export default React.memo(InputNumber);